import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { paginateQuery } from '@/lib/paginateSupabase';

export function useAdminDashboardData() {
  return useQuery({
    queryKey: ['admin-dashboard-data'],
    queryFn: async () => {
      const orgsRes = await supabase.from('organizations').select('id, name, logo_url').order('name');
      if (orgsRes.error) throw orgsRes.error;

      const [profiles, projects, evaluations, mentorRecords, cohortParticipants] = await Promise.all([
        paginateQuery<{ id: string; organization_id: string | null }>((from, to) =>
          supabase.from('profiles').select('id, organization_id').range(from, to)
        ),
        paginateQuery<{ id: string; user_id: string; status: string | null; submitted: boolean | null }>((from, to) =>
          supabase.from('participant_projects').select('id, user_id, status, submitted').range(from, to)
        ),
        paginateQuery<{ id: string; project_id: string }>((from, to) =>
          supabase.from('project_evaluations').select('id, project_id').range(from, to)
        ),
        paginateQuery<any>((from, to) =>
          supabase.from('cohort_mentors').select(`
            user_id,
            cohort_id,
            cohorts!cohort_mentors_cohort_id_fkey(organization_id),
            profiles!cohort_mentors_user_id_fkey(id, full_name, avatar_url)
          `).range(from, to)
        ),
        paginateQuery<{ cohort_id: string; user_id: string }>((from, to) =>
          supabase.from('cohort_participants').select('cohort_id, user_id').range(from, to)
        ),
      ]);

      return {
        organizations: orgsRes.data ?? [],
        profiles,
        projects,
        evaluations,
        mentorRecords,
        cohortParticipants,
      };
    },
  });
}

const STATUS_PROGRESS: Record<string, number> = {
  ideation: 20,
  in_progress: 50,
  completed: 100,
  paused: 10,
};

export function computeOrgStats(
  data: NonNullable<ReturnType<typeof useAdminDashboardData>['data']>,
  orgId: string | null,
) {
  const orgProfiles = orgId
    ? data.profiles.filter(p => p.organization_id === orgId)
    : data.profiles;
  const profileIds = new Set(orgProfiles.map(p => p.id));

  const orgProjects = data.projects.filter(p => profileIds.has(p.user_id));
  const evaluatedIds = new Set(data.evaluations.map(e => e.project_id));

  const totalParticipants = orgProfiles.length;
  const demodayReadyCount = orgProjects.filter(p => p.submitted).length;
  const usersWithProject = new Set(orgProjects.map(p => p.user_id));
  const atRiskCount = orgProfiles.filter(p => !usersWithProject.has(p.id)).length;

  const avgProgress = orgProjects.length > 0
    ? Math.round(
        orgProjects.reduce(
          (acc, p) =>
            acc + (p.submitted ? 100 : (STATUS_PROGRESS[p.status ?? 'ideation'] ?? 20)),
          0,
        ) / orgProjects.length,
      )
    : 0;

  const totalDeliverables = orgProjects.length;
  const validatedDeliverables = orgProjects.filter(p => evaluatedIds.has(p.id)).length;

  return {
    totalParticipants,
    demodayReadyCount,
    atRiskCount,
    avgProgress,
    totalDeliverables,
    validatedDeliverables,
  };
}

export function computeMentorsForOrg(
  data: NonNullable<ReturnType<typeof useAdminDashboardData>['data']>,
  orgId: string | null,
) {
  let records = data.mentorRecords;
  if (orgId) {
    records = records.filter((m: any) => (m.cohorts as any)?.organization_id === orgId);
  }

  const mentorMap = new Map<string, { profile: any; cohortIds: Set<string> }>();
  records.forEach((m: any) => {
    if (!mentorMap.has(m.user_id)) {
      mentorMap.set(m.user_id, { profile: m.profiles, cohortIds: new Set() });
    }
    mentorMap.get(m.user_id)!.cohortIds.add(m.cohort_id);
  });

  return Array.from(mentorMap.entries()).map(([userId, { profile, cohortIds }]) => {
    const participantCount = data.cohortParticipants.filter(cp =>
      cohortIds.has(cp.cohort_id),
    ).length;
    return {
      id: userId,
      name: profile?.full_name ?? 'Sin nombre',
      avatar: profile?.avatar_url ?? '',
      participantCount,
    };
  });
}
